import React, { useEffect, useId, useState } from 'react';

import { INTERACTIVE_ANALYTICS } from '../../../../content/staticAssets';

const ASSETS = INTERACTIVE_ANALYTICS.commandHub;

const SATELLITES = [
  { id: 'chat', src: ASSETS.chat },
  { id: 'report', src: ASSETS.report },
  { id: 'chart', src: ASSETS.chart },
];

/** Hub → all icons one-by-one → all connectors one-by-one → flow pulses */
const HUB_LEAD_MS = 700;
const ICON_STEP_MS = 850;
const ICON_SETTLE_MS = 950;
const CONNECTOR_STEP_MS = 900;
const CONNECTOR_DRAW_MS = 950;

const VB = 200;
const CX = 100;
const CY = 100;
const ORBIT_R = 65;

const FLOW_PULSES = 2;
const FLOW_DUR = 2.4;
const BUBBLE_R = 3;
const RIM_OFFSET = 1.35;

function quadPoint(t, p0, p1, p2) {
  const mt = 1 - t;
  return {
    x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
    y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y,
  };
}

function quadTangent(t, p0, p1, p2) {
  const dx = 2 * (1 - t) * (p1.x - p0.x) + 2 * t * (p2.x - p1.x);
  const dy = 2 * (1 - t) * (p1.y - p0.y) + 2 * t * (p2.y - p1.y);
  const len = Math.hypot(dx, dy) || 1;
  return { x: dx / len, y: dy / len };
}

function sampleQuadRimPaths(p0, p1, p2, offset = RIM_OFFSET, steps = 28) {
  const left = [];
  const right = [];

  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const pt = quadPoint(t, p0, p1, p2);
    const tan = quadTangent(t, p0, p1, p2);
    const nx = -tan.y;
    const ny = tan.x;
    left.push({ x: pt.x + nx * offset, y: pt.y + ny * offset });
    right.push({ x: pt.x - nx * offset, y: pt.y - ny * offset });
  }

  const toPath = (pts) =>
    pts
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(' ');

  return { left: toPath(left), right: toPath(right) };
}

function FlowBubble({ path, begin, gradId }) {
  return (
    <g className="ch-hub-orbit__bubble-wrap">
      <circle className="ch-hub-orbit__bubble-body" r={BUBBLE_R} cx="0" cy="0" fill={`url(#${gradId}-bubble)`} />
      <ellipse
        className="ch-hub-orbit__bubble-shine"
        cx="0"
        cy={-BUBBLE_R + 0.55}
        rx={BUBBLE_R * 0.52}
        ry="0.42"
        fill={`url(#${gradId}-edge-shine)`}
      />
      <ellipse
        className="ch-hub-orbit__bubble-shine"
        cx="0"
        cy={BUBBLE_R - 0.55}
        rx={BUBBLE_R * 0.52}
        ry="0.42"
        fill={`url(#${gradId}-edge-shine)`}
      />
      <animateMotion
        dur={`${FLOW_DUR}s`}
        begin={`${begin}s`}
        repeatCount="indefinite"
        path={path}
        keyPoints="0;1"
        keyTimes="0;1"
        calcMode="linear"
        rotate="auto"
      />
    </g>
  );
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function useInView(threshold = 0.15) {
  const ref = React.useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold, rootMargin: '0px 0px -4% 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

function satellitePoint(index) {
  const angle = (index * 120 * Math.PI) / 180;
  return {
    x: CX + ORBIT_R * Math.sin(angle),
    y: CY - ORBIT_R * Math.cos(angle),
  };
}

function connectorMeta(x, y, index) {
  const sx = x + (CX - x) * 0.06;
  const sy = y + (CY - y) * 0.06;
  const ex = x + (CX - x) * 0.86;
  const ey = y + (CY - y) * 0.86;
  const perpX = index === 0 ? -4 : (x - CX) * 0.08;
  const perpY = index === 0 ? 0 : (y - CY) * 0.08;
  const mx = x + (CX - x) * 0.5 + perpX;
  const my = y + (CY - y) * 0.5 + perpY;
  const fmt = (n) => n.toFixed(2);
  const p0 = { x: sx, y: sy };
  const p1 = { x: mx, y: my };
  const p2 = { x: ex, y: ey };
  const rims = sampleQuadRimPaths(p0, p1, p2);

  return {
    path: `M ${fmt(sx)} ${fmt(sy)} Q ${fmt(mx)} ${fmt(my)} ${fmt(ex)} ${fmt(ey)}`,
    p0,
    p1,
    p2,
    rimLeft: rims.left,
    rimRight: rims.right,
  };
}

function ConnectorFadeGradient({ id, p0, p2 }) {
  return (
    <linearGradient id={id} gradientUnits="userSpaceOnUse" x1={p0.x} y1={p0.y} x2={p2.x} y2={p2.y}>
      <stop offset="0%" stopColor="#19e3f2" stopOpacity="0" />
      <stop offset="11%" stopColor="#19e3f2" stopOpacity="0.82" />
      <stop offset="55%" stopColor="#22e8f7" stopOpacity="0.82" />
      <stop offset="100%" stopColor="#19e3f2" stopOpacity="0.82" />
    </linearGradient>
  );
}

function ConnectorShineGradient({ id, p0, p2 }) {
  return (
    <linearGradient id={id} gradientUnits="userSpaceOnUse" x1={p0.x} y1={p0.y} x2={p2.x} y2={p2.y}>
      <stop offset="0%" stopColor="#b8fbff" stopOpacity="0" />
      <stop offset="11%" stopColor="#b8fbff" stopOpacity="0.28" />
      <stop offset="100%" stopColor="#b8fbff" stopOpacity="0.28" />
    </linearGradient>
  );
}

/**
 * Command hub: all surrounding icons appear first, then connectors in order, then flow pulses.
 */
export default function CommandHubOrbitAnimation() {
  const uid = useId().replace(/:/g, '');
  const reducedMotion = usePrefersReducedMotion();
  const { ref, inView } = useInView();
  const [phase, setPhase] = useState('idle');
  const [visibleIcons, setVisibleIcons] = useState(0);
  const [visibleConnectors, setVisibleConnectors] = useState(0);

  useEffect(() => {
    if (!inView) {
      setPhase('idle');
      setVisibleIcons(0);
      setVisibleConnectors(0);
      return undefined;
    }

    if (reducedMotion) {
      setPhase('flow');
      setVisibleIcons(SATELLITES.length);
      setVisibleConnectors(SATELLITES.length);
      return undefined;
    }

    setPhase('enter');
    setVisibleIcons(0);
    setVisibleConnectors(0);

    const timers = [];
    let t = HUB_LEAD_MS;

    SATELLITES.forEach((_, index) => {
      timers.push(setTimeout(() => setVisibleIcons(index + 1), t));
      t += ICON_STEP_MS;
    });

    t += ICON_SETTLE_MS;

    SATELLITES.forEach((_, index) => {
      timers.push(setTimeout(() => setVisibleConnectors(index + 1), t));
      t += CONNECTOR_STEP_MS;
    });

    timers.push(setTimeout(() => setPhase('flow'), t + CONNECTOR_DRAW_MS));

    return () => timers.forEach(clearTimeout);
  }, [inView, reducedMotion]);

  const showFlow = phase === 'flow' && !reducedMotion;

  return (
    <div ref={ref} className="ch-hub-orbit ia-diagram w-full min-w-0" aria-hidden="true">
      <div
        className={[
          'ch-hub-orbit__stage',
          phase !== 'idle' ? 'is-active' : '',
          `is-phase-${phase}`,
          reducedMotion ? 'is-reduced' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <svg
          className="ch-hub-orbit__connectors"
          viewBox={`0 0 ${VB} ${VB}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <radialGradient id={`${uid}-bubble`} cx="38%" cy="32%" r="68%">
              <stop offset="0%" stopColor="#c9fcff" stopOpacity="0.95" />
              <stop offset="45%" stopColor="#19e3f2" stopOpacity="0.82" />
              <stop offset="100%" stopColor="#14c9d8" stopOpacity="0.82" />
            </radialGradient>
            <radialGradient id={`${uid}-edge-shine`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#d8feff" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#19e3f2" stopOpacity="0" />
            </radialGradient>
          </defs>
          {SATELLITES.map((sat, index) => {
            const { x, y } = satellitePoint(index);
            const { path, p0, p2, rimLeft, rimRight } = connectorMeta(x, y, index);
            const lineGrad = `${uid}-line-${index}`;
            const shineGrad = `${uid}-shine-${index}`;
            const isConnectorVisible = visibleConnectors > index;

            return (
              <g key={sat.id} className={showFlow && isConnectorVisible ? 'is-flowing' : ''}>
                <defs>
                  <ConnectorFadeGradient id={lineGrad} p0={p0} p2={p2} />
                  <ConnectorShineGradient id={shineGrad} p0={p0} p2={p2} />
                </defs>
                <path
                  key={isConnectorVisible ? `${sat.id}-draw` : `${sat.id}-idle`}
                  className={[
                    'ch-hub-orbit__tube',
                    isConnectorVisible ? 'is-drawing' : '',
                    showFlow && isConnectorVisible ? 'is-flowing' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  d={path}
                  fill="none"
                  stroke={`url(#${lineGrad})`}
                  strokeLinecap="round"
                  pathLength="100"
                  style={{ animationDelay: `${index * 0.35}s` }}
                />
                {isConnectorVisible && (
                  <>
                    <path
                      className="ch-hub-orbit__tube-shine"
                      d={rimLeft}
                      fill="none"
                      stroke={`url(#${shineGrad})`}
                      strokeLinecap="round"
                    />
                    <path
                      className="ch-hub-orbit__tube-shine"
                      d={rimRight}
                      fill="none"
                      stroke={`url(#${shineGrad})`}
                      strokeLinecap="round"
                    />
                  </>
                )}
                {showFlow &&
                  Array.from({ length: FLOW_PULSES }, (_, pulseIdx) => (
                    <FlowBubble
                      key={`${sat.id}-bubble-${pulseIdx}`}
                      gradId={uid}
                      path={path}
                      begin={index * 0.55 + pulseIdx * 1.15}
                    />
                  ))}
              </g>
            );
          })}
        </svg>

        <div className="ch-hub-orbit__hub-wrap">
          <div className={`ch-hub-orbit__hub-glow ${phase === 'flow' ? 'is-pulsing' : ''}`} aria-hidden="true" />
          <img src={ASSETS.hub} alt="" className="ch-hub-orbit__hub-img" draggable={false} />
        </div>

        <div className="ch-hub-orbit__satellites">
          {SATELLITES.map((sat, index) => (
            <div
              key={sat.id}
              className={[
                'ch-hub-orbit__sat-slot',
                `ch-hub-orbit__sat-slot--${index}`,
                visibleIcons > index ? 'is-visible' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <img src={sat.src} alt="" className="ch-hub-orbit__sat-img" draggable={false} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
